import { Controller } from "@hotwired/stimulus";

/**
 * Handles form submission with loading states when Turbo is disabled
 *
 * Sometimes we need to disable Turbo on form submissions (e.g., when redirecting to a different subdomain).
 * When we do this, we lose Turbo's automatic button disable behavior during submission.
 * This controller fills that gap by manually handling the button state.
 *
 * @example
 * ```ruby
 * button_to some_path, class: "btn", data: {
 *   turbo: false,
 *   controller: "turbo-disabled-form-submitter",
 *   action: "click->turbo-disabled-form-submitter#disable"
 * } do
 *   span class: "loading loading-spinner hidden",
 *       data: { turbo_disabled_form_submitter_target: "spinner" }
 *   span data: { turbo_disabled_form_submitter_target: "text" } do
 *     plain "Submit"
 *   end
 *   img data: { turbo_disabled_form_submitter_target: "image" }
 * end
 * ```
 */
export default class extends Controller {
  static values = {
    formId: {
      type: String,
      default: null,
    },
  };

  /** @type {Element[]} targets - Elements that will be toggled during loading state */
  static targets = [
    /** The loading spinner element that will be shown during submission */
    "spinner",
    /** The text content that will remain visible during submission */
    "text",
    /** The image container that will be blurred during submission */
    "image",
    /** The content overlay that will be hidden during submission */
    "content",
  ];

  /**
   * Handles the form submission and updates UI state
   * @param {Event} event - The click event
   */
  disable(event) {
    event.preventDefault();

    const button = event.currentTarget;
    let form;

    if (this.formIdValue) {
      form = document.getElementById(this.formIdValue);
    } else {
      form = button.closest("form");
    }

    button.disabled = true;
    this.spinnerTarget.classList.remove("hidden");

    if (this.hasContentTarget) {
      this.contentTarget.classList.add("hidden");
    }

    if (this.hasImageTarget) {
      this.imageTarget.classList.remove("group-hover:blur-none");
      this.imageTarget.classList.remove("blur-none");
      this.imageTarget.classList.add("blur-[2px]");
    }

    form.submit();
  }
}
